// Single-source API client. credentials: include keeps the httpOnly cookie flowing.

import type {
  ApiEnvelope,
  Category,
  FeatureFlag,
  FeatureFlagKey,
  IntelLayerKey,
  NotificationTemplate,
  NotificationTemplateKey,
  RegistryRow,
  RequestRecord,
  Risk,
  Role,
  SectionKey,
  Status,
  StatusEvent,
  User,
  VendorIntelV2,
  VendorReviewerNote,
} from '@air/shared';

export type IntelProvider = 'anthropic' | 'google';

export interface IntelLayerProviderInfo {
  layer: 'evidence' | 'threat';
  provider: IntelProvider;
  model: string;
  anthropicConfigured: boolean;
  googleConfigured: boolean;
}

export interface IntelModelEntry {
  id: string;
  name: string;
  description?: string;
}

export interface IntelModelCatalog {
  anthropic: IntelModelEntry[];
  google: IntelModelEntry[];
  anthropicConfigured: boolean;
  googleConfigured: boolean;
}

export interface IntelLayerPromptInfo {
  layer: 'evidence' | 'threat';
  prompt: string;
  isDefault: boolean;
  defaultPrompt: string;
}

export interface PublicConfig {
  itContactEmail: string;
  legalContactEmail: string;
  slaItHours: number;
  slaLegalHours: number;
  slaTermLabel: string;
  flags: Record<FeatureFlagKey, boolean>;
}

export interface SlaSettings {
  itHours: number;
  legalHours: number;
  termLabel: string;
}

export interface SlaPatch {
  itHours?: number;
  legalHours?: number;
  termLabel?: string;
}

const BASE = '/api/v1';

class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, 'parse_error', 'Bad server response.');
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? `HTTP ${res.status}`, err?.details);
  }
  return (json as ApiEnvelope<T>).data;
}

export { ApiError };

// ── auth ──
export const api = {
  async login(email: string, password: string): Promise<User> {
    return call<User>('POST', '/auth/login', { email, password });
  },
  async logout(): Promise<void> {
    await call<void>('POST', '/auth/logout');
  },
  async me(): Promise<User> {
    return call<User>('GET', '/auth/me');
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await call<void>('POST', '/auth/change-password', { currentPassword, newPassword });
  },

  // ── requests ──
  async createRequest(input: NewRequestInput): Promise<{ request: RequestRecord; events: StatusEvent[] }> {
    return call('POST', '/requests', input);
  },
  async myRequests(): Promise<RequestRecord[]> {
    return call('GET', '/requests/mine');
  },
  async getRequest(id: string): Promise<{ request: RequestRecord; events: StatusEvent[] }> {
    return call('GET', `/requests/${id}`);
  },
  async respond(id: string, comment: string): Promise<{ request: RequestRecord; events: StatusEvent[] }> {
    return call('POST', `/requests/${id}/respond`, { comment });
  },

  // ── registry ──
  async registry(
    params: {
      status?: Status;
      bucket?: 'approved' | 'pending' | 'rejected' | 'all';
      category?: Category;
      risk?: Risk;
      q?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<{ rows: RegistryRow[]; total: number }> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.bucket) qs.set('bucket', params.bucket);
    if (params.category) qs.set('category', params.category);
    if (params.risk) qs.set('risk', params.risk);
    if (params.q) qs.set('q', params.q);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const res = await fetch(`${BASE}/registry?${qs.toString()}`, { credentials: 'include' });
    if (!res.ok) throw new ApiError(res.status, 'registry_error', 'Failed to load registry.');
    const json = (await res.json()) as ApiEnvelope<RegistryRow[]>;
    return { rows: json.data, total: (json.meta?.total as number) ?? json.data.length };
  },
  async registryCounts(): Promise<RegistryCounts> {
    return call<RegistryCounts>('GET', '/registry/counts');
  },
  async lookup(name: string): Promise<LookupHit[]> {
    return call<LookupHit[]>('GET', `/registry/lookup?name=${encodeURIComponent(name)}`);
  },

  // ── reviews ──
  async itQueue(): Promise<QueueRow[]> {
    return call('GET', '/reviews/it/queue');
  },
  async legalQueue(): Promise<QueueRow[]> {
    return call('GET', '/reviews/legal/queue');
  },
  async queueBundle(role: 'it' | 'legal'): Promise<QueueBundle> {
    return call<QueueBundle>('GET', `/reviews/${role}/all`);
  },
  async itAction(
    id: string,
    action: 'approve' | 'reject' | 'request-clarification',
    body: { comment?: string } = {},
  ) {
    return call('POST', `/reviews/requests/${id}/it/${action}`, body);
  },
  async legalAction(
    id: string,
    action: 'approve' | 'approve-with-conditions' | 'reject' | 'request-clarification',
    body: { comment?: string; conditions?: string } = {},
  ) {
    return call('POST', `/reviews/requests/${id}/legal/${action}`, body);
  },

  // ── admin ──
  async listUsers(): Promise<AdminUser[]> {
    return call('GET', '/admin/users');
  },
  async createUser(input: { email: string; fullName: string; role: Role }): Promise<AdminUser & { tempPassword: string }> {
    return call('POST', '/admin/users', input);
  },
  async patchUser(id: string, patch: { fullName?: string; role?: Role; isActive?: boolean }): Promise<void> {
    await call<void>('PATCH', `/admin/users/${id}`, patch);
  },
  async deleteUser(id: string): Promise<void> {
    await call<void>('DELETE', `/admin/users/${id}`);
  },
  async adminResetPassword(
    id: string,
    mode: 'email' | 'temp_password',
  ): Promise<{ mode: 'email' | 'temp_password'; email: string; tempPassword?: string }> {
    return call('POST', `/admin/users/${id}/reset-password`, { mode });
  },
  async metrics(): Promise<AdminMetrics> {
    return call('GET', '/admin/metrics');
  },
  async adminAuditLog(limit = 40): Promise<AdminAuditEntry[]> {
    return call<AdminAuditEntry[]>('GET', `/admin/audit?limit=${limit}`);
  },
  async deleteRequest(id: string): Promise<void> {
    await call<void>('DELETE', `/admin/requests/${id}`);
  },
  async adminEditRequest(
    id: string,
    patch: AdminEditRequestInput,
  ): Promise<{ request: RequestRecord; events: StatusEvent[] }> {
    return call('PATCH', `/admin/requests/${id}`, patch);
  },

  // ── public config + feature flags ──
  async publicConfig(): Promise<PublicConfig> {
    return call<PublicConfig>('GET', '/config/public');
  },
  async featureFlags(): Promise<Record<FeatureFlagKey, boolean>> {
    return call<Record<FeatureFlagKey, boolean>>('GET', '/feature-flags');
  },
  async adminListFeatureFlags(): Promise<FeatureFlag[]> {
    return call<FeatureFlag[]>('GET', '/admin/feature-flags');
  },
  async adminSetFeatureFlag(key: FeatureFlagKey, value: boolean): Promise<FeatureFlag> {
    return call<FeatureFlag>('PATCH', `/admin/feature-flags/${key}`, { value });
  },

  // ── notification templates (admin-editable email templates) ──
  async adminListNotificationTemplates(): Promise<NotificationTemplate[]> {
    return call<NotificationTemplate[]>('GET', '/admin/notification-templates');
  },
  async adminGetNotificationTemplate(
    key: NotificationTemplateKey,
  ): Promise<NotificationTemplate & { defaultSubject: string; defaultBody: string }> {
    return call<NotificationTemplate & { defaultSubject: string; defaultBody: string }>(
      'GET',
      `/admin/notification-templates/${key}`,
    );
  },
  async adminSaveNotificationTemplate(
    key: NotificationTemplateKey,
    patch: { subject?: string; body?: string; enabled?: boolean },
  ): Promise<NotificationTemplate> {
    return call<NotificationTemplate>('PATCH', `/admin/notification-templates/${key}`, patch);
  },
  async adminResetNotificationTemplate(
    key: NotificationTemplateKey,
  ): Promise<NotificationTemplate> {
    return call<NotificationTemplate>('POST', `/admin/notification-templates/${key}/reset`);
  },
  async adminTestNotificationTemplate(
    key: NotificationTemplateKey,
    toEmail: string,
    previewVariables: Record<string, string>,
  ): Promise<{ queued: true }> {
    return call<{ queued: true }>(
      'POST',
      `/admin/notification-templates/${key}/test`,
      { toEmail, previewVariables },
    );
  },

  // ── intel provider + model (per-layer, admin-tunable) ──
  async adminGetLayerProvider(
    layer: 'evidence' | 'threat',
  ): Promise<IntelLayerProviderInfo> {
    return call<IntelLayerProviderInfo>('GET', `/admin/intel-provider/${layer}`);
  },
  async adminSetLayerProvider(
    layer: 'evidence' | 'threat',
    provider: IntelProvider,
    model?: string,
  ): Promise<IntelLayerProviderInfo> {
    return call<IntelLayerProviderInfo>(
      'PATCH',
      `/admin/intel-provider/${layer}`,
      model ? { provider, model } : { provider },
    );
  },
  async adminListIntelModels(): Promise<IntelModelCatalog> {
    return call<IntelModelCatalog>('GET', '/admin/intel-models');
  },
  async adminRefreshIntelModels(): Promise<IntelModelCatalog> {
    return call<IntelModelCatalog>('POST', '/admin/intel-models/refresh');
  },
  async adminGetLayerPrompt(layer: 'evidence' | 'threat'): Promise<IntelLayerPromptInfo> {
    return call<IntelLayerPromptInfo>('GET', `/admin/intel-prompt/${layer}`);
  },
  async adminSetLayerPrompt(
    layer: 'evidence' | 'threat',
    prompt: string,
  ): Promise<IntelLayerPromptInfo> {
    return call<IntelLayerPromptInfo>('PATCH', `/admin/intel-prompt/${layer}`, { prompt });
  },
  async adminResetLayerPrompt(layer: 'evidence' | 'threat'): Promise<IntelLayerPromptInfo> {
    return call<IntelLayerPromptInfo>('PATCH', `/admin/intel-prompt/${layer}`, { reset: true });
  },

  // ── SLA thresholds (admin-tunable review-stage time budgets) ──
  async adminGetSla(): Promise<SlaSettings> {
    return call<SlaSettings>('GET', '/admin/sla');
  },
  async adminSetSla(patch: SlaPatch): Promise<SlaSettings> {
    return call<SlaSettings>('PATCH', '/admin/sla', patch);
  },

  // ── vendor intel + reviewer notes ──
  async vendorIntel(vendor: string): Promise<VendorIntelV2 | null> {
    return call<VendorIntelV2 | null>('GET', `/vendor-intel/${encodeURIComponent(vendor)}`);
  },
  async regenerateVendorIntel(
    vendor: string,
    websiteUrl?: string | null,
  ): Promise<VendorIntelV2> {
    return call<VendorIntelV2>(
      'POST',
      `/vendor-intel/${encodeURIComponent(vendor)}/regenerate`,
      websiteUrl ? { websiteUrl } : undefined,
    );
  },
  async regenerateVendorIntelLayer(
    vendor: string,
    layer: IntelLayerKey,
    websiteUrl?: string | null,
  ): Promise<VendorIntelV2> {
    return call<VendorIntelV2>(
      'POST',
      `/vendor-intel/${encodeURIComponent(vendor)}/regenerate/${layer}`,
      websiteUrl ? { websiteUrl } : undefined,
    );
  },
  async updateVendorNote(vendor: string, section: SectionKey, note: string): Promise<VendorReviewerNote> {
    return call<VendorReviewerNote>(
      'PUT',
      `/vendor-intel/${encodeURIComponent(vendor)}/notes/${section}`,
      { note },
    );
  },
};

export interface AdminEditRequestInput {
  reason: string;
  softwareName?: string;
  vendor?: string | null;
  category?: Category;
  logoUrl?: string | null;
  websiteUrl?: string;
  intendedUse?: string;
  dataInputs?: string;
  expectedOutputs?: string;
  outputUsage?: string;
  usesConfidentialData?: boolean;
  usesPrivilegedData?: boolean;
  usesCustomerData?: boolean;
  additionalNotes?: string | null;
}

export interface NewRequestInput {
  softwareName: string;
  vendor?: string | null;
  category: Category;
  logoUrl?: string | null;
  websiteUrl: string;
  intendedUse: string;
  dataInputs: string;
  expectedOutputs: string;
  usesConfidentialData: boolean;
  usesPrivilegedData: boolean;
  usesCustomerData: boolean;
  outputUsage: string;
  additionalNotes?: string | null;
}

export interface LookupHit {
  id: string;
  ticketNumber: string;
  softwareName: string;
  status: Status;
  updatedAt: string;
}

export interface QueueRow {
  id: string;
  ticketNumber: string;
  softwareName: string;
  status: Status;
  submitterName: string;
  createdAt: string;
  updatedAt: string;
}

export interface FullQueueRow {
  id: string;
  ticketNumber: string;
  softwareName: string;
  vendor: string | null;
  category: Category;
  status: Status;
  risk: Risk;
  submitterName: string;
  submitterEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface QueueBundle {
  open: FullQueueRow[];
  waiting: FullQueueRow[];
  recent: FullQueueRow[];
  stats: {
    open: number;
    waiting: number;
    overdue: number;
    slaHours: number;
    slaTermLabel: string;
    medianDays: number | null;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  isActive: boolean;
  mustChangePassword: boolean;
  createdAt: string;
}

export interface AdminMetrics {
  total: number;
  byStatus: Record<string, number>;
  medianItSeconds: number | null;
  medianLegalSeconds: number | null;
  weekly: Array<{ weekStart: string; count: number }>;
  reviewers: Array<{
    id: string;
    fullName: string;
    role: 'it_reviewer' | 'legal_reviewer';
    email: string;
    reviewedCount: number;
    medianSeconds: number | null;
    openCount: number;
  }>;
}

export interface AdminAuditEntry {
  id: string;
  createdAt: string;
  eventType: string;
  fromStatus: string | null;
  toStatus: string;
  comment: string | null;
  actorRole: string;
  actorName: string;
  ticketNumber: string;
  requestId: string;
}

export interface RegistryCounts {
  approved: number;
  pending: number;
  rejected: number;
  all: number;
}
